import React, { useState, useEffect } from 'react';
import './AddProductForm.css';
import ShopConfigTable from './ShopConfigTable';
import ProductFormByType from './forms/ProductFormByType';
import { useAuth } from '../context/AuthContext';

// Hilfsfunktion um Domain aus URL zu extrahieren
const extractDomain = (url) => {
  if (!url) return 'Shop';
  try {
    const urlObj = new URL(url);
    // Entferne "www." wenn vorhanden
    return urlObj.hostname.replace(/^www\./, '');
  } catch {
    return 'Shop';
  }
};

function ProductManager({ productId, onProductUpdated }) {
  const { fetchWithAuth } = useAuth();
  const [product, setProduct] = useState(null);
  const [productTypes, setProductTypes] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [formData, setFormData] = useState({
    // Basis-Felder
    name: '',
    description: '',
    productTypeId: '',
    manufacturerId: '',
    image: '',
    datasheet: '',
    ean: '',
    gtin: '',
    upc: '',
    phases: [],
    
    // Beleuchtungs-Felder
    watts: '',
    umolPerSecond: '',
    efficiency: '',
    recommendedArea: '',
    growTentSize: '',
    lifespan: '',
    lifespanType: 'L80',
    smartControl: '',
    dimmable: '',
    dimmerIncluded: false,
    
    // Luft & Klima-Felder
    airFlowRate: '',
    noiseLevel: '',
    powerConsumption: '',
    temperatureControl: '',
    humidityControl: '',
    connections: '',
    tankVolume: '',
    climateSmartControl: false,
    climateDescription: '',
    
    // Messtechnik-Felder
    measurementType: '',
    measurementRange: '',
    accuracy: '',
    connectivity: '',
    displayType: '',
    batteryType: '',
    measurementFeatures: '',
    
    // Growzelt-Felder
    tentSize: '',
    tentHeight: '',
    tentArea: '',
    material: '',
    reflectivity: '',
    thickness: '',
    framework: '',
    hasFloor: false,
    tentFeatures: '',

    // Dünger-Felder
    fertilizerProductLine: '',
    fertilizerType: '',
    fertilizerFormulation: '',
    fertilizerPackageSizes: [],
    fertilizerNpk: '',
    fertilizerNutrients: [],
    fertilizerHumicAcids: false,
    fertilizerFulvicAcids: false,
    fertilizerPhaseSuitability: [],
    fertilizerSystemCompatibility: [],
    fertilizerAutoIrrigationSuitable: false,
    fertilizerPhRange: '',
    fertilizerEcStandardDose: '',
    fertilizerDosage: '',
    fertilizerApplicationFrequency: '',
    fertilizerFoliarPossible: false,
    fertilizerComboProducts: '',
    fertilizerNutrientScheduleLink: '',
    fertilizerBottleSizeLiters: '',
    fertilizerDosagePerLiterNum: '',
    fertilizerDosageVeg: '',
    fertilizerDosageBloom: '',
    fertilizerVegSchedule: '',
    fertilizerBloomSchedule: '',
    fertilizerPricePerBottle: '',
    fertilizerPricePerLiter: '',
    fertilizerSolutionPerEuro: ''
  });

  const [shopConfigs, setShopConfigs] = useState([]);
  const [testingSelectors, setTestingSelectors] = useState({});
  const [testResults, setTestResults] = useState({});
  const [priceSelectionModal, setPriceSelectionModal] = useState(null);
  const [selectedPriceIndex, setSelectedPriceIndex] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (productId) {
      fetchProduct();
    }

    // Event Listener für URL Updates vom URL Scraper
    const handleUrlsUpdated = (event) => {
      if (event.detail.productId === productId) {
        console.log('🔄 Product URLs wurden aktualisiert, lade Daten neu...');
        fetchProduct();
      }
    };

    window.addEventListener('productUrlsUpdated', handleUrlsUpdated);

    return () => {
      window.removeEventListener('productUrlsUpdated', handleUrlsUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const fetchProduct = async () => {
    try {
      // Lade Produkt, Shops, Produkttypen und Hersteller parallel
      const [productRes, shopsRes, typesRes, manufacturersRes] = await Promise.all([
        fetch(`/api/products/${productId}`),
        fetch('/api/stores'),
        fetch('/api/product-types'),
        fetch('/api/manufacturers')
      ]);
      
      const data = await productRes.json();
      const allShops = await shopsRes.json();
      const typesData = await typesRes.json();
      const manufacturersData = await manufacturersRes.json();
      
      const prod = data.product;
      setProduct(prod);
      setProductTypes(Array.isArray(typesData) ? typesData : []);
      setManufacturers(Array.isArray(manufacturersData) ? manufacturersData : []);
      
      // Extrahiere Phasen und normalisiere (erste Buchstabe groß)
      const phases = (prod.categories || []).filter(c => c.type === 'Phase').map(c => {
        const name = c.name || '';
        return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
      });
      
      setFormData({
        // Basis-Felder
        name: prod.name || '',
        description: prod.description || '',
        productTypeId: (prod.productTypeId?._id || prod.productTypeId) || '',
        manufacturerId: (prod.manufacturerId?._id || prod.manufacturerId) || '',
        image: prod.image || '',
        datasheet: prod.datasheet || '',
        ean: prod.ean || '',
        gtin: prod.gtin || '',
        upc: prod.upc || '',
        phases: phases,
        
        // Beleuchtungs-Felder (Legacy oder lightingSpecs)
        watts: (prod.lightingSpecs?.watts || prod.watts) ? String(prod.lightingSpecs?.watts || prod.watts) : '',
        umolPerSecond: (prod.lightingSpecs?.umolPerSecond || prod.umolPerSecond) ? String(prod.lightingSpecs?.umolPerSecond || prod.umolPerSecond) : '',
        efficiency: (prod.lightingSpecs?.efficiency || prod.efficiency) ? String(prod.lightingSpecs?.efficiency || prod.efficiency) : '',
        recommendedArea: (prod.lightingSpecs?.recommendedArea || prod.recommendedArea) ? String(prod.lightingSpecs?.recommendedArea || prod.recommendedArea) : '',
        growTentSize: prod.lightingSpecs?.growTentSize || prod.growTentSize || '',
        lifespan: (prod.lightingSpecs?.lifespan || prod.lifespan) ? String(prod.lightingSpecs?.lifespan || prod.lifespan) : '',
        lifespanType: prod.lightingSpecs?.lifespanType || prod.lifespanType || 'L80',
        smartControl: prod.lightingSpecs?.smartControl || prod.smartControl || '',
        dimmable: prod.lightingSpecs?.dimmable || prod.dimmable || '',
        dimmerIncluded: prod.lightingSpecs?.dimmerIncluded || prod.dimmerIncluded || false,
        
        // Luft & Klima-Felder
        airFlowRate: (prod.climateSpecs?.airFlowRate) ? String(prod.climateSpecs.airFlowRate) : '',
        noiseLevel: (prod.climateSpecs?.noiseLevel) ? String(prod.climateSpecs.noiseLevel) : '',
        powerConsumption: (prod.climateSpecs?.powerConsumption) ? String(prod.climateSpecs.powerConsumption) : '',
        temperatureControl: prod.climateSpecs?.temperatureControl || '',
        humidityControl: prod.climateSpecs?.humidityControl || '',
        connections: prod.climateSpecs?.connections || '',
        tankVolume: (prod.climateSpecs?.tankVolume) ? String(prod.climateSpecs.tankVolume) : '',
        climateSmartControl: prod.climateSpecs?.smartControl || false,
        climateDescription: prod.climateSpecs?.climateDescription || '',
        
        // Messtechnik-Felder
        measurementType: prod.measurementSpecs?.measurementType || '',
        measurementRange: prod.measurementSpecs?.measurementRange || '',
        accuracy: prod.measurementSpecs?.accuracy || '',
        connectivity: prod.measurementSpecs?.connectivity || '',
        displayType: prod.measurementSpecs?.displayType || '',
        batteryType: prod.measurementSpecs?.batteryType || '',
        measurementFeatures: prod.measurementSpecs?.measurementFeatures || '',
        
        // Growzelt-Felder
        tentSize: prod.tentSpecs?.tentSize || '',
        tentHeight: (prod.tentSpecs?.tentHeight) ? String(prod.tentSpecs.tentHeight) : '',
        tentArea: (prod.tentSpecs?.tentArea) ? String(prod.tentSpecs.tentArea) : '',
        material: prod.tentSpecs?.material || '',
        reflectivity: (prod.tentSpecs?.reflectivity) ? String(prod.tentSpecs.reflectivity) : '',
        thickness: (prod.tentSpecs?.thickness) ? String(prod.tentSpecs.thickness) : '',
        framework: prod.tentSpecs?.framework || '',
        hasFloor: prod.tentSpecs?.hasFloor || false,
        tentFeatures: prod.tentSpecs?.tentFeatures || '',

        // Dünger-Felder
        fertilizerProductLine: prod.fertilizerSpecs?.productLine || '',
        fertilizerType: prod.fertilizerSpecs?.fertilizerType || '',
        fertilizerFormulation: prod.fertilizerSpecs?.formulation || '',
        fertilizerPackageSizes: prod.fertilizerSpecs?.packageSizes || [],
        fertilizerNpk: prod.fertilizerSpecs?.npk || '',
        fertilizerNutrients: prod.fertilizerSpecs?.fertilizerNutrients || [],
        fertilizerHumicAcids: prod.fertilizerSpecs?.hasHumicAcids || false,
        fertilizerFulvicAcids: prod.fertilizerSpecs?.hasFulvicAcids || false,
        fertilizerPhaseSuitability: prod.fertilizerSpecs?.phaseSuitability || [],
        fertilizerSystemCompatibility: prod.fertilizerSpecs?.systemCompatibility || [],
        fertilizerAutoIrrigationSuitable: prod.fertilizerSpecs?.autoIrrigationSuitable || false,
        fertilizerPhRange: prod.fertilizerSpecs?.phRange || '',
        fertilizerEcStandardDose: prod.fertilizerSpecs?.ecStandardDose || '',
        fertilizerDosage: prod.fertilizerSpecs?.dosageMlPerLiter || '',
        fertilizerApplicationFrequency: prod.fertilizerSpecs?.applicationFrequency || '',
        fertilizerFoliarPossible: prod.fertilizerSpecs?.foliarPossible || false,
        fertilizerComboProducts: (prod.fertilizerSpecs?.comboProducts || []).join(', '),
        fertilizerNutrientScheduleLink: prod.fertilizerSpecs?.nutrientScheduleLink || '',
        fertilizerBottleSizeLiters: (prod.fertilizerSpecs?.bottleSizeLiters !== undefined && prod.fertilizerSpecs?.bottleSizeLiters !== null)
          ? String(prod.fertilizerSpecs.bottleSizeLiters)
          : '',
        fertilizerDosagePerLiterNum: (prod.fertilizerSpecs?.dosagePerLiterNum !== undefined && prod.fertilizerSpecs?.dosagePerLiterNum !== null)
          ? String(prod.fertilizerSpecs.dosagePerLiterNum)
          : '',
        fertilizerDosageVeg: (prod.fertilizerSpecs?.dosageVegMlPerLiter !== undefined && prod.fertilizerSpecs?.dosageVegMlPerLiter !== null)
          ? String(prod.fertilizerSpecs.dosageVegMlPerLiter)
          : '',
        fertilizerDosageBloom: (prod.fertilizerSpecs?.dosageBloomMlPerLiter !== undefined && prod.fertilizerSpecs?.dosageBloomMlPerLiter !== null)
          ? String(prod.fertilizerSpecs.dosageBloomMlPerLiter)
          : '',
        fertilizerVegSchedule: prod.fertilizerSpecs?.vegSchedule || '',
        fertilizerBloomSchedule: prod.fertilizerSpecs?.bloomSchedule || '',
        fertilizerPricePerBottle: (prod.fertilizerSpecs?.pricePerBottle !== undefined && prod.fertilizerSpecs?.pricePerBottle !== null)
          ? String(prod.fertilizerSpecs.pricePerBottle)
          : '',
        fertilizerPricePerLiter: (prod.fertilizerSpecs?.pricePerLiter !== undefined && prod.fertilizerSpecs?.pricePerLiter !== null)
          ? String(prod.fertilizerSpecs.pricePerLiter)
          : '',
        fertilizerSolutionPerEuro: (prod.fertilizerSpecs?.nutrientSolutionPerEuro !== undefined && prod.fertilizerSpecs?.nutrientSolutionPerEuro !== null)
          ? String(prod.fertilizerSpecs.nutrientSolutionPerEuro)
          : ''
      });

      // ALLE Shops anzeigen - nicht nur die mit Daten!
      // Erstelle zuerst eine Map mit den vorhandenen Shop-Daten aus dem Produkt
      const productShopsMap = new Map();
      
      (prod.shops || []).forEach(shop => {
        const storeId = shop.storeId?._id || shop.storeId;
        // Speichere JEDEN Shop, auch wenn er keine URL hat (wurde gelöscht)
        productShopsMap.set(storeId, shop);
      });

      // Preise laden für deliveryTime
      const pricesMap = new Map();
      if (data.prices && data.prices.length > 0) {
        data.prices.forEach(price => {
          if (price.storeId) {
            const storeIdStr = typeof price.storeId === 'object' ? price.storeId._id : price.storeId;
            pricesMap.set(storeIdStr.toString(), price);
          }
        });
      }
      
      // Konvertiere ALLE Shops zu Configs Array
      // Zeige jeden Shop aus der Datenbank, auch wenn er noch nicht konfiguriert ist
      const configs = allShops.map(store => {
        const existingShopData = productShopsMap.get(store._id);
        const priceData = pricesMap.get(store._id.toString());
        
        if (existingShopData) {
          // Shop ist bereits konfiguriert - verwende vorhandene Daten
          return {
            storeId: store._id,
            shopName: store.name,
            logo: store.logo || '',
            productUrl: existingShopData.productUrl || '',
            storeWebsite: store.website || '',
            priceSelector: existingShopData.priceSelector || '',
            titleSelector: existingShopData.titleSelector || '',
            imageSelector: existingShopData.imageSelector || '',
            inStockSelector: existingShopData.inStockSelector || '',
            active: !!(existingShopData.productUrl && existingShopData.productUrl.trim()),
            lastScrapedPrice: priceData?.price || existingShopData.lastScrapedPrice,
            lastScrapedAt: priceData?.scrapedAt || existingShopData.lastScrapedAt,
            deliveryTime: priceData?.deliveryTime || '',
            status: (existingShopData.productUrl && existingShopData.priceSelector) ? 'valid' : 'inactive'
          };
        } else {
          // Shop ist noch nicht konfiguriert - erstelle leeren Eintrag
          return {
            storeId: store._id,
            shopName: store.name,
            logo: store.logo || '',
            productUrl: '',
            storeWebsite: store.website || '',
            priceSelector: '',
            titleSelector: '',
            imageSelector: '',
            inStockSelector: '',
            active: false,
            deliveryTime: '',
            status: 'inactive'
          };
        }
      });

      // Setze status auf 'loading' für Shops mit URL
      const configsWithLoadingStatus = configs.map(config => ({
        ...config,
        status: config.productUrl ? 'loading' : config.status
      }));
      
      setShopConfigs(configsWithLoadingStatus);
      
      // Auto-Preise laden für Shops mit URL
      configs.forEach(config => {
        if (config.productUrl) {
          setTimeout(() => autoTestSelector(config.storeId, config.productUrl), 500);
        }
      });
    } catch (err) {
      console.error('❌ Fehler beim Laden:', err);
      setError('Fehler beim Laden des Produkts');
    }
  };

  // Mapping von Zeltgröße zu Fläche (m²)
  const growTentSizeToArea = {
    '40x40': '0.16',
    '40x60': '0.24',
    '40x80': '0.32',
    '40x100': '0.40',
    '60x60': '0.36',
    '60x100': '0.60',
    '60x120': '0.72',
    '60x150': '0.90',
    '80x80': '0.64',
    '80x100': '0.80',
    '80x120': '0.96',
    '80x150': '1.20',
    '80x200': '1.60',
    '100x100': '1.00',
    '100x150': '1.50',
    '100x200': '2.00',
    '120x120': '1.44',
    '120x150': '1.80',
    '120x180': '2.16',
    '120x200': '2.40',
    '120x240': '2.88',
    '150x150': '2.25',
    '150x200': '3.00',
    '150x240': '3.60',
    '200x200': '4.00',
    '240x240': '5.76'
  };

  // Reverse Mapping von Fläche zu Zeltgröße
  const areaToGrowTentSize = Object.entries(growTentSizeToArea).reduce((acc, [size, area]) => {
    acc[area] = size;
    return acc;
  }, {});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newFormData = {
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    };

    // Wenn Growzelt-Größe geändert wird, automatisch die Fläche setzen (nur bei Beleuchtung)
    if (name === 'growTentSize' && growTentSizeToArea[value]) {
      newFormData.recommendedArea = growTentSizeToArea[value];
    }

    // Wenn Empfohlene Fläche geändert wird, automatisch die Zeltgröße setzen (nur bei Beleuchtung)
    if (name === 'recommendedArea' && areaToGrowTentSize[value]) {
      newFormData.growTentSize = areaToGrowTentSize[value];
    }

    setFormData(newFormData);
  };

  const handlePhaseChange = (phase) => {
    setFormData(prev => ({
      ...prev,
      phases: prev.phases.includes(phase)
        ? prev.phases.filter(p => p !== phase)
        : [...prev.phases, phase]
    }));
  };

  const handleArrayChange = (fieldName, value) => {
    setFormData(prev => {
      const current = Array.isArray(prev[fieldName]) ? prev[fieldName] : [];
      const updated = current.includes(value)
        ? current.filter(item => item !== value)
        : [...current, value];

      return {
        ...prev,
        [fieldName]: updated
      };
    });
  };

  const autoTestSelector = async (storeId, productUrl) => {
    try {
      const res = await fetchWithAuth('/api/products/find-price', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productUrl })
      });

      const data = await res.json();
      
      if (data.found) {
        let selectedPrice = data.selectedPrice;
        
        if (!selectedPrice && data.prices && data.prices.length > 0) {
          const sortedPrices = [...data.prices].sort((a, b) => {
            const priceA = parseFloat(a.priceText?.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
            const priceB = parseFloat(b.priceText?.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
            return priceB - priceA;
          });
          selectedPrice = sortedPrices[0];
        }
        
        if (selectedPrice) {
          setShopConfigs(prev =>
            prev.map(s =>
              s.storeId === storeId 
                ? { 
                    ...s, 
                    priceSelector: selectedPrice.priceSelector,
                    status: 'valid'
                  }
                : s
            )
          );
          setTestResults(prev => ({ ...prev, [storeId]: { found: true, price: selectedPrice.price, priceText: selectedPrice.priceText } }));
        }
      } else {
        setTestResults(prev => ({ ...prev, [storeId]: { found: false, message: 'Preis nicht gefunden' } }));
      }
    } catch (err) {
      console.error('Auto test error:', err);
    }
  };

  const handleShopChange = (storeId, field, value) => {
    setShopConfigs(prev =>
      prev.map(shop => {
        if (shop.storeId === storeId) {
          const updated = { ...shop, [field]: value };

          if (field === 'priceSelector' || field === 'productUrl') {
            if (!updated.productUrl || !updated.priceSelector) {
              updated.status = 'inactive';
            } else {
              try {
                const isValidSelector = updated.priceSelector.trim().length > 0 && 
                  /^[.#[-_a-zA-Z0-9\s:,>~+]+$/.test(updated.priceSelector);
                updated.status = isValidSelector ? 'valid' : 'error';
              } catch {
                updated.status = 'error';
              }
            }
          }
          return updated;
        }
        return shop;
      })
    );
  };

  const handleTestSelector = async (storeId) => {
    const shop = shopConfigs.find(s => s.storeId === storeId);
    if (!shop || !shop.productUrl) {
      alert('Bitte geben Sie die Produkt-URL ein');
      return;
    }

    // Status auf LOADING setzen (orange)
    setShopConfigs(prev => {
      const updated = prev.map(s =>
        s.storeId === storeId 
          ? { ...s, status: 'loading' }
          : s
      );
      return updated;
    });
    
    setTestingSelectors(prev => ({ ...prev, [storeId]: true }));
    setTestResults(prev => ({ ...prev, [storeId]: { loading: true, message: 'Wird geladen...' } }));

    try {
      const res = await fetchWithAuth('/api/products/find-price', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productUrl: shop.productUrl,
          productId: productId,  // Sende productId mit
          storeId: storeId       // Sende storeId mit
        })
      });

      const data = await res.json();
      
      if (data.found) {
        // Automatisch den gefundenen Preis nehmen
        let selectedPrice = data.selectedPrice;
        
        if (!selectedPrice && data.prices && data.prices.length > 0) {
          selectedPrice = data.prices[0];
        }
        
        if (selectedPrice) {
          // Status auf VALID setzen (grün) ERST WENN PREIS GEFUNDEN
          setShopConfigs(prev =>
            prev.map(s =>
              s.storeId === storeId 
                ? { 
                    ...s, 
                    priceSelector: selectedPrice.priceSelector,
                    status: 'valid'  // ERST HIER auf grün
                  }
                : s
            )
          );
          
          // Zeige Erfolg mit Speicher-Status
          if (data.saved) {
            setTestResults(prev => ({ 
              ...prev, 
              [storeId]: { 
                found: true, 
                price: selectedPrice.price, 
                priceText: selectedPrice.priceText,
                saved: true,
                message: '✅ €' + selectedPrice.price.toFixed(2) + ' gespeichert'
              } 
            }));
          } else {
            setTestResults(prev => ({ 
              ...prev, 
              [storeId]: { 
                found: true, 
                price: selectedPrice.price, 
                priceText: selectedPrice.priceText,
                message: '€' + selectedPrice.price.toFixed(2)
              } 
            }));
          }
        }
      } else {
        // Status auf ERROR setzen (rot)
        setShopConfigs(prev =>
          prev.map(s =>
            s.storeId === storeId 
              ? { ...s, status: 'error' }
              : s
          )
        );
        setTestResults(prev => ({ 
          ...prev, 
          [storeId]: { 
            found: false, 
            message: '❌ ' + (data.message || 'Preis nicht gefunden') 
          } 
        }));
      }
    } catch (err) {
      // Status auf ERROR setzen (rot)
      setShopConfigs(prev =>
        prev.map(s =>
          s.storeId === storeId 
            ? { ...s, status: 'error' }
            : s
        )
      );
      console.error('Test error:', err);
      setTestResults(prev => ({ 
        ...prev, 
        [storeId]: { 
          found: false, 
          message: '❌ Fehler: ' + err.message 
        } 
      }));
    } finally {
      setTestingSelectors(prev => ({ ...prev, [storeId]: false }));
    }
  };

  const handleConfirmPrice = () => {
    if (!priceSelectionModal) return;

    const selectedPrice = priceSelectionModal.prices[selectedPriceIndex];
    const storeId = priceSelectionModal.storeId;

    setShopConfigs(prev =>
      prev.map(s =>
        s.storeId === storeId 
          ? { 
              ...s, 
              priceSelector: selectedPrice.priceSelector,
              status: 'valid'
            }
          : s
      )
    );

    setTestResults(prev => ({ 
      ...prev, 
      [storeId]: { found: true, price: selectedPrice.price, priceText: selectedPrice.priceText } 
    }));

    setPriceSelectionModal(null);
  };

  const handleRemoveShop = async (storeId) => {
    if (!window.confirm('Diesen Shop wirklich entfernen?')) return;

    try {
      const res = await fetchWithAuth(`/api/products/${productId}/shop/${storeId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        setShopConfigs(prev => prev.filter(s => s.storeId !== storeId));
        setSuccessMessage('✅ Shop entfernt!');
        setTimeout(() => setSuccessMessage(''), 2000);
      }
    } catch (err) {
      setError('Fehler: ' + err.message);
    }
  };

  const handleDeleteLink = async (storeId) => {
    if (!window.confirm('Link und letzten gescrapten Preis wirklich löschen?')) return;

    try {
      setLoading(true);
      const res = await fetchWithAuth(`/api/products/${productId}/shop/${storeId}/link`, {
        method: 'DELETE'
      });

      if (res.ok) {
        const updatedProduct = await res.json();
        
        // Hole alle verfügbaren Stores um die komplette shopConfigs Liste zu rekonstruieren
        // (nicht nur die im Produkt gespeicherten, sondern ALLE möglichen Shops)
        const storesRes = await fetch('/api/stores');
        const allShops = await storesRes.json();
        
        // Erstelle Map der Shop-Daten aus dem aktualisierten Produkt
        const productShopsMap = new Map();
        (updatedProduct.shops || []).forEach(shop => {
          const storeId = shop.storeId?._id || shop.storeId;
          productShopsMap.set(storeId, shop);
        });

        // Rekonstruiere ALLE shopConfigs (wie in fetchProduct)
        const configs = allShops.map(store => {
          const existingShopData = productShopsMap.get(store._id);
          
          if (existingShopData) {
            // Shop ist konfiguriert - verwende Daten aus Produkt
            return {
              storeId: store._id,
              shopName: store.name,
              logo: store.logo || '',
              productUrl: existingShopData.productUrl || '',
              storeWebsite: store.website || '',
              priceSelector: existingShopData.priceSelector || '',
              titleSelector: existingShopData.titleSelector || '',
              imageSelector: existingShopData.imageSelector || '',
              inStockSelector: existingShopData.inStockSelector || '',
              active: !!(existingShopData.productUrl && existingShopData.productUrl.trim()),
              lastScrapedPrice: existingShopData.lastScrapedPrice,
              lastScrapedAt: existingShopData.lastScrapedAt,
              status: (existingShopData.productUrl && existingShopData.priceSelector) ? 'valid' : 'inactive'
            };
          } else {
            // Shop ist nicht konfiguriert - erstelle leeren Eintrag
            return {
              storeId: store._id,
              shopName: store.name,
              logo: store.logo || '',
              productUrl: '',
              storeWebsite: store.website || '',
              priceSelector: '',
              titleSelector: '',
              imageSelector: '',
              inStockSelector: '',
              active: false,
              status: 'inactive'
            };
          }
        });
        
        setShopConfigs(configs);
        
        // Entferne test results für diesen Shop
        setTestResults(prev => {
          const newResults = { ...prev };
          delete newResults[storeId];
          return newResults;
        });
        
        setSuccessMessage('✅ Link und Preis gelöscht!');
        setTimeout(() => setSuccessMessage(''), 2000);
      }
    } catch (err) {
      setError('Fehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (storeId) => {
    try {
      setLoading(true);
      const res = await fetchWithAuth(`/api/products/${productId}/shop/${storeId}/toggle-active`, {
        method: 'PATCH'
      });

      if (res.ok) {
        const updatedProduct = await res.json();
        
        // Update shopConfigs basierend auf API response
        const updatedShops = updatedProduct.shops || [];
        const configs = updatedShops.map(shop => ({
          storeId: shop.storeId?._id || shop.storeId,
          shopName: shop.storeId?.name || '',
          logo: shop.storeId?.logo || '',
          productUrl: shop.productUrl || '',
          storeWebsite: shop.storeId?.website || '',
          priceSelector: shop.priceSelector || '',
          titleSelector: shop.titleSelector || '',
          imageSelector: shop.imageSelector || '',
          inStockSelector: shop.inStockSelector || '',
          active: shop.active !== undefined ? shop.active : true,
          lastScrapedPrice: shop.lastScrapedPrice,
          lastScrapedAt: shop.lastScrapedAt,
          status: (shop.productUrl && shop.priceSelector) ? 'valid' : 'inactive'
        }));
        
        setShopConfigs(configs);
        
        const shop = updatedShops.find(s => 
          (s.storeId?._id || s.storeId).toString() === storeId
        );
        const isActive = shop?.active;
        
        setSuccessMessage(`✅ Shop ${isActive ? 'aktiviert' : 'deaktiviert'}!`);
        setTimeout(() => setSuccessMessage(''), 2000);
      }
    } catch (err) {
      setError('Fehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAutoSearchAllPrices = async (currentShopConfigs) => {
    const shopsToSearch = currentShopConfigs.filter(s => s.productUrl && !s.priceSelector);
    
    if (shopsToSearch.length === 0) {
      return currentShopConfigs;
    }

    let updatedConfigs = [...currentShopConfigs];
    const newTestResults = {};

    // Suche Preise für alle Shops sequenziell
    for (const shop of shopsToSearch) {
      setTestingSelectors(prev => ({ ...prev, [shop.storeId]: true }));

      try {
        const res = await fetchWithAuth('/api/products/find-price', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            productUrl: shop.productUrl
          })
        });

        const data = await res.json();
        
        if (data.found) {
          let selectedPrice = data.selectedPrice;
          
          if (!selectedPrice && data.prices && data.prices.length > 0) {
            // Nimm den höchsten Preis
            const sortedPrices = [...data.prices].sort((a, b) => {
              const priceA = parseFloat(a.priceText?.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
              const priceB = parseFloat(b.priceText?.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
              return priceB - priceA;
            });
            selectedPrice = sortedPrices[0];
          }
          
          if (selectedPrice) {
            // Update die lokale updatedConfigs Kopie
            updatedConfigs = updatedConfigs.map(s =>
              s.storeId === shop.storeId 
                ? { 
                    ...s, 
                    priceSelector: selectedPrice.priceSelector,
                    status: 'valid'
                  }
                : s
            );
            newTestResults[shop.storeId] = { found: true, price: selectedPrice.price, priceText: selectedPrice.priceText };
          }
        } else {
          newTestResults[shop.storeId] = { found: false, message: 'Kein Preis gefunden' };
        }
      } catch (err) {
        newTestResults[shop.storeId] = { found: false, message: 'Fehler: ' + err.message };
      } finally {
        setTestingSelectors(prev => ({ ...prev, [shop.storeId]: false }));
      }

      // Kleine Pause zwischen Requests
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    // Update die UI mit allen neuen Ergebnissen
    setTestResults(prev => ({ ...prev, ...newTestResults }));
    setShopConfigs(updatedConfigs);

    return updatedConfigs;
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Produktname ist erforderlich');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');

      // SCHRITT 1: Automatisch alle Preise suchen
      const updatedConfigs = await handleAutoSearchAllPrices(shopConfigs);

      // SCHRITT 2: Mit aktualisierten shopConfigs weitermachen
      
      // WICHTIG: Speichere ALLE Shops, nicht nur die mit vollständiger Config!
      // Shops mit produktUrl und priceSelector = aktiv und werden gesucht
      // Shops ohne = sind deaktiviert aber müssen erhalten bleiben
      const shopsWithActiveConfig = updatedConfigs.filter(s => s.priceSelector && s.productUrl);

      if (shopsWithActiveConfig.length === 0) {
        setError('Bitte konfigurieren Sie mindestens einen Shop oder aktivieren Sie die Preissuche');
        setLoading(false);
        return;
      }

      const productData = {
        name: formData.name,
        description: formData.description,
        image: formData.image,
        datasheet: (formData.datasheet && formData.datasheet.trim()) ? formData.datasheet : undefined,
        ean: formData.ean || undefined,
        gtin: formData.gtin || undefined,
        upc: formData.upc || undefined,
        productTypeId: formData.productTypeId || undefined,
        manufacturerId: formData.manufacturerId || undefined,
        categories: formData.phases.map(phase => ({
          type: 'Phase',
          name: phase
        })),
        shops: updatedConfigs.map(s => ({
          storeId: s.storeId,
          shopName: s.shopName,
          productUrl: s.productUrl || '',
          priceSelector: s.priceSelector || '',
          titleSelector: s.titleSelector || '',
          imageSelector: s.imageSelector || '',
          inStockSelector: s.inStockSelector || ''
        }))
      };

      // Helper: Finde den Produkttyp-Namen
      const getProductTypeName = () => {
        const type = productTypes.find(t => t._id === formData.productTypeId);
        return type?.name;
      };

      const typeName = getProductTypeName();

      // Ergänze produkttyp-spezifische Felder
      if (typeName === 'Beleuchtung') {
        productData.lightingSpecs = {
          watts: formData.watts ? parseFloat(formData.watts) : undefined,
          umolPerSecond: formData.umolPerSecond ? parseFloat(formData.umolPerSecond) : undefined,
          efficiency: formData.efficiency ? parseFloat(formData.efficiency) : undefined,
          recommendedArea: formData.recommendedArea ? parseFloat(formData.recommendedArea) : undefined,
          growTentSize: formData.growTentSize || undefined,
          lifespan: formData.lifespan ? parseFloat(formData.lifespan) : undefined,
          lifespanType: formData.lifespanType || 'L80',
          smartControl: formData.smartControl || undefined,
          dimmable: formData.dimmable || undefined,
          dimmerIncluded: formData.dimmerIncluded || false
        };
        
        // Legacy-Kompatibilität
        productData.watts = formData.watts ? parseFloat(formData.watts) : undefined;
        productData.umolPerSecond = formData.umolPerSecond ? parseFloat(formData.umolPerSecond) : undefined;
        productData.efficiency = formData.efficiency ? parseFloat(formData.efficiency) : undefined;
        productData.recommendedArea = formData.recommendedArea ? parseFloat(formData.recommendedArea) : undefined;
        productData.growTentSize = formData.growTentSize || undefined;
        productData.lifespan = formData.lifespan ? parseFloat(formData.lifespan) : undefined;
        productData.lifespanType = formData.lifespanType || 'L80';
        productData.smartControl = formData.smartControl || undefined;
        productData.dimmable = formData.dimmable || undefined;
        productData.dimmerIncluded = formData.dimmerIncluded || false;
      } else if (typeName === 'Luft & Klima') {
        productData.climateSpecs = {
          airFlowRate: formData.airFlowRate ? parseFloat(formData.airFlowRate) : undefined,
          noiseLevel: formData.noiseLevel ? parseFloat(formData.noiseLevel) : undefined,
          powerConsumption: formData.powerConsumption ? parseFloat(formData.powerConsumption) : undefined,
          temperatureControl: formData.temperatureControl || undefined,
          humidityControl: formData.humidityControl || undefined,
          connections: formData.connections || undefined,
          tankVolume: formData.tankVolume ? parseFloat(formData.tankVolume) : undefined,
          smartControl: formData.climateSmartControl || false,
          climateDescription: formData.climateDescription || undefined
        };
      } else if (typeName === 'Messtechnik') {
        productData.measurementSpecs = {
          measurementType: formData.measurementType || undefined,
          measurementRange: formData.measurementRange || undefined,
          accuracy: formData.accuracy || undefined,
          connectivity: formData.connectivity || undefined,
          displayType: formData.displayType || undefined,
          batteryType: formData.batteryType || undefined,
          measurementFeatures: formData.measurementFeatures || undefined
        };
      } else if (typeName === 'Growzelte') {
        productData.tentSpecs = {
          tentSize: formData.tentSize || undefined,
          tentHeight: formData.tentHeight ? parseFloat(formData.tentHeight) : undefined,
          tentArea: formData.tentArea ? parseFloat(formData.tentArea) : undefined,
          material: formData.material || undefined,
          reflectivity: formData.reflectivity ? parseFloat(formData.reflectivity) : undefined,
          thickness: formData.thickness ? parseFloat(formData.thickness) : undefined,
          framework: formData.framework || undefined,
          hasFloor: formData.hasFloor || false,
          tentFeatures: formData.tentFeatures || undefined
        };
      } else if (typeName === 'Dünger') {
        const comboProducts = (formData.fertilizerComboProducts || '')
          .split(',')
          .map(item => item.trim())
          .filter(Boolean);

        productData.fertilizerSpecs = {
          productLine: formData.fertilizerProductLine || undefined,
          fertilizerType: formData.fertilizerType || undefined,
          formulation: formData.fertilizerFormulation || undefined,
          packageSizes: formData.fertilizerPackageSizes || [],
          npk: formData.fertilizerNpk || undefined,
          nutrients: formData.fertilizerNutrients || [], // Legacy: Für Kompatibilität
          fertilizerNutrients: formData.fertilizerNutrients || [], // Neu: Für Checkboxen
          hasHumicAcids: formData.fertilizerHumicAcids || false,
          hasFulvicAcids: formData.fertilizerFulvicAcids || false,
          phaseSuitability: formData.fertilizerPhaseSuitability || [],
          systemCompatibility: formData.fertilizerSystemCompatibility || [],
          autoIrrigationSuitable: formData.fertilizerAutoIrrigationSuitable || false,
          phRange: formData.fertilizerPhRange || undefined,
          ecStandardDose: formData.fertilizerEcStandardDose || undefined,
          dosageMlPerLiter: formData.fertilizerDosage || undefined,
          applicationFrequency: formData.fertilizerApplicationFrequency || undefined,
          foliarPossible: formData.fertilizerFoliarPossible || false,
          comboProducts: comboProducts,
          nutrientScheduleLink: formData.fertilizerNutrientScheduleLink || undefined,
          bottleSizeLiters: formData.fertilizerBottleSizeLiters ? parseFloat(formData.fertilizerBottleSizeLiters) : undefined,
          dosagePerLiterNum: formData.fertilizerDosagePerLiterNum ? parseFloat(formData.fertilizerDosagePerLiterNum) : undefined,
          dosageVegMlPerLiter: formData.fertilizerDosageVeg ? parseFloat(formData.fertilizerDosageVeg) : undefined,
          dosageBloomMlPerLiter: formData.fertilizerDosageBloom ? parseFloat(formData.fertilizerDosageBloom) : undefined,
          vegSchedule: formData.fertilizerVegSchedule || undefined,
          bloomSchedule: formData.fertilizerBloomSchedule || undefined,
          pricePerBottle: formData.fertilizerPricePerBottle ? parseFloat(formData.fertilizerPricePerBottle) : undefined,
          pricePerLiter: formData.fertilizerPricePerLiter ? parseFloat(formData.fertilizerPricePerLiter) : undefined,
          nutrientSolutionPerEuro: formData.fertilizerSolutionPerEuro ? parseFloat(formData.fertilizerSolutionPerEuro) : undefined
        };
      }

      const response = await fetchWithAuth(`/api/products/${productId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(productData)
      });

      if (response.ok) {
        const updatedProduct = await response.json();

        // SCHRITT 3: Speichere alle gefundenen Preise in die DB
        let pricesSaved = 0;
        
        for (const [storeId, testResult] of Object.entries(testResults)) {
          if (testResult?.found && testResult?.price) {
            try {
              const shopConfig = updatedConfigs.find(s => s.storeId === storeId);
              const retailer = shopConfig ? extractDomain(shopConfig.productUrl) : 'Unknown';
              
              const priceResponse = await fetchWithAuth('/api/prices', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  productId: updatedProduct._id || productId,
                  retailer: retailer,
                  price: parseFloat(testResult.price),
                  currency: 'EUR',
                  url: shopConfig?.productUrl,
                  inStock: true,
                  productImage: formData.image
                })
              });
              
              if (priceResponse.ok) {
                pricesSaved++;
              }
            } catch (err) {
              // Fehler beim Speichern stillschweigend ignorieren
            }
          }
        }

        setSuccessMessage(`✅ "${formData.name}" erfolgreich gespeichert! (${pricesSaved} Preise aktualisiert)`);
        setTimeout(() => {
          // Lade das Produkt neu um die neuen Werte anzuzeigen
          console.log('🔄 Lade Produkt nach Speichern neu...');
          fetchProduct().then(() => {
            console.log('✅ Produkt nach Speichern neu geladen!');
            onProductUpdated?.();
            setSuccessMessage('');
          }).catch(err => {
            console.error('❌ Fehler beim Neu-Laden:', err);
            setSuccessMessage('');
          });
        }, 500); // Reduziere von 2000 auf 500ms
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || 'Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving:', error);
      setError('Fehler: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

if (!product) {
    return <div className="loading-message">⏳ Produkt wird geladen...</div>;
  }

  return (
    <form className="add-product-form" onSubmit={handleSaveProduct}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0 }}>✏️ '{formData.name}' bearbeiten</h2>
        <button 
          type="button" 
          onClick={() => {
            setSuccessMessage('');
            setError('');
            fetchProduct();
          }}
          style={{
            background: 'rgba(0, 136, 255, 0.2)',
            border: '1px solid rgba(0, 136, 255, 0.5)',
            color: '#66b3ff',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}
        >
          🔄 Daten neu laden
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {/* Basis-Infos */}
      <div className="product-info-section">
        <h3>📦 Produkt-Informationen</h3>

        <div className="form-group">
          <label htmlFor="name">Produktname *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="z.B. SANlight EVO 3-60"
            required
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Beschreibung</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Optionale Produktbeschreibung..."
            disabled={loading}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="productTypeId">Produkttyp</label>
            <select
              id="productTypeId"
              name="productTypeId"
              value={formData.productTypeId}
              onChange={handleChange}
              disabled={loading}
            >
              <option value="">-- Bitte wählen --</option>
              {productTypes.map(type => (
                <option key={type._id} value={type._id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="manufacturerId">Hersteller</label>
            <select
              id="manufacturerId"
              name="manufacturerId"
              value={formData.manufacturerId}
              onChange={handleChange}
              disabled={loading}
            >
              <option value="">-- Bitte wählen --</option>
              {manufacturers.map(mfg => (
                <option key={mfg._id} value={mfg._id}>
                  {mfg.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="datasheet">📄 Datenblatt-URL</label>
            <input
              type="url"
              id="datasheet"
              name="datasheet"
              value={formData.datasheet}
              onChange={handleChange}
              placeholder="https://..."
              disabled={loading}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="image">🖼️ Bild-URL</label>
            <input
              type="url"
              id="image"
              name="image"
              value={formData.image}
              onChange={handleChange}
              placeholder="https://..."
              disabled={loading}
            />
          </div>
        </div>
      </div>

      {/* Produkttyp-spezifische Felder */}
      <ProductFormByType 
        selectedProductTypeId={formData.productTypeId}
        productTypes={productTypes}
        formData={formData}
        handleChange={handleChange}
        handlePhaseChange={handlePhaseChange}
        handleArrayChange={handleArrayChange}
        disabled={loading}
      />

      <div>
        {/* EAN/GTIN/UPC Codes für Produktidentifikation */}
        <div className="form-row" style={{ display: 'flex', gap: '1rem' }}>
          <div className="form-group" style={{ flex: 1 }}>
            <label htmlFor="ean">🏷️ EAN/GTIN-13</label>
            <input
              type="text"
              id="ean"
              name="ean"
              value={formData.ean}
              onChange={handleChange}
              placeholder="z.B. 9120105733471"
              maxLength={13}
              disabled={loading}
            />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label htmlFor="gtin">📊 GTIN-14</label>
            <input
              type="text"
              id="gtin"
              name="gtin"
              value={formData.gtin}
              onChange={handleChange}
              placeholder="z.B. 09120105733471"
              maxLength={14}
              disabled={loading}
            />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label htmlFor="upc">🇺🇸 UPC</label>
            <input
              type="text"
              id="upc"
              name="upc"
              value={formData.upc}
              onChange={handleChange}
              placeholder="12-stellig (USA)"
              maxLength={12}
              disabled={loading}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="image">🖼️ Bild-URL</label>
            <input
              type="url"
              id="image"
              name="image"
              value={formData.image}
              onChange={handleChange}
              placeholder="https://..."
              disabled={loading}
            />
          </div>
        </div>

        {/* DEPRECATED: Diese Beleuchtungsfelder sind nur Legacy und sollten NICHT gerendert werden. Alle Felder werden jetzt über ProductFormByType gerendert. */}
      </div>

      {/* Shop-Konfigurationen - Neue Tabelle */}
      <div className="shop-prices-section">
        <ShopConfigTable 
          shopConfigs={shopConfigs}
          onShopChange={handleShopChange}
          onDeleteLink={handleDeleteLink}
          onToggleActive={handleToggleActive}
          onTestSelector={handleTestSelector}
          loading={loading}
          testingSelectors={testingSelectors}
          testResults={testResults}
        />
        
        <p className="shop-help-text" style={{ marginTop: '1.5rem' }}>
          💡 Gib die Shop-URL in der Tabelle ein und klick 🔍 um den Preis automatisch zu finden
        </p>
      </div>

      <button type="submit" className="btn btn-primary btn-large" disabled={loading || shopConfigs.length === 0}>
        {loading ? '⏳ Wird gespeichert und Preise werden gesucht...' : '💾 Änderungen speichern (Preise werden automatisch gesucht)'}
      </button>

      {/* Preisauswahl-Modal */}
      {priceSelectionModal && (
        <div className="modal-overlay" onClick={() => setPriceSelectionModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>💰 Mehrere Preise gefunden - Bitte wählen</h3>
            <p>{priceSelectionModal.message}</p>
            
            <div className="price-options">
              {priceSelectionModal.prices.map((price, idx) => (
                <label key={idx} className={`price-option ${selectedPriceIndex === idx ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="price-selection"
                    value={idx}
                    checked={selectedPriceIndex === idx}
                    onChange={() => setSelectedPriceIndex(idx)}
                  />
                  <span className="price-info">
                    <strong>{price.price.toFixed(2)}€</strong>
                    <small>{price.priceText}</small>
                    <tiny className="selector-info">Selektor: {price.priceSelector}</tiny>
                  </span>
                </label>
              ))}
            </div>

            <div className="modal-actions">
              <button 
                type="button"
                className="btn btn-secondary"
                onClick={() => setPriceSelectionModal(null)}
              >
                ❌ Abbrechen
              </button>
              <button 
                type="button"
                className="btn btn-primary"
                onClick={handleConfirmPrice}
              >
                ✅ Preis bestätigen
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}

export default ProductManager;

